import CryptoJS from "crypto-js";
import {static as Immutable} from "seamless-immutable";

const LOCAL_STORAGE_UPDATED_IN_OTHER_TAB = "LOCAL_STORAGE_UPDATED_IN_OTHER_TAB";

/**
 * Action creator for when the browser signals that local storage has been updated in another browser tab.
 * @param {string} nameInStorage
 * @param {string} encryptedData
 * @return {{type: string, nameInStorage: string, encryptedData: string}}
 */
export function localStorageUpdatedInOtherTab(nameInStorage, encryptedData) {
    return {
        type: LOCAL_STORAGE_UPDATED_IN_OTHER_TAB,
        nameInStorage,
        encryptedData
    };
}

const SALT = "kiha3268sdUIASDlkn5349";

const getSecret = (flush) => {
    let publicKey = localStorage.getItem("publicKey");
    if (flush || !publicKey) {
        localStorage.clear();
        publicKey = CryptoJS.SHA256(`iouwerknvdasoiwueqrnvdasnkjaer:${new Date().toDateString()}`);
        localStorage.setItem("publicKey", publicKey);
    }

    return `${publicKey}${SALT}`;
};

/**
 * Wraps a reducer function which, if the state has been changed on any of the keys which are specified for storage, stores the new state in localStorage.
 * Loads the default state (for instance on page load) from the store.
 * @param {Function} reducer
 * @param {Function} defaultState
 * @param {string} nameInStorage
 * @param {string[]} rootKeysToStore
 * @return {Function}
 */
export function localStorageWritingReducer(reducer, defaultState, nameInStorage, rootKeysToStore) {
    return (state, action) => {
        if (action.type == LOCAL_STORAGE_UPDATED_IN_OTHER_TAB && nameInStorage === action.nameInStorage) {
            let newData;
            try {
                newData = JSON.parse(CryptoJS.AES.decrypt(action.encryptedData, getSecret()).toString(CryptoJS.enc.Utf8));
                return Immutable.merge(state || defaultState(), newData, {deep: true});
            } catch (error) {
                throw new Error("Could not parse local storage data passed from other tab.");
            }
        }

        if (!state) {
            let storedData;
            try {
                storedData = JSON.parse(CryptoJS.AES.decrypt(localStorage.getItem(nameInStorage), getSecret()).toString(CryptoJS.enc.Utf8));
            } catch (error) {
                storedData = {};
            } finally {
                state = Immutable.merge(defaultState(), storedData, {deep: true});
            }
        }
        const newState = reducer(state, action);

        if (rootKeysToStore.some(k => newState[k] !== state[k])) {
            const toStore = Object.assign({}, ...rootKeysToStore.map(k => ({
                [k]: newState[k]
            })));
            localStorage.setItem(nameInStorage, CryptoJS.AES.encrypt(JSON.stringify(toStore), getSecret()).toString());
        }

        return newState;
    };
}

const resetVolatile = data => {
    if (Array.isArray(data)) {
        for (const elem of data) {
            resetVolatile(elem);
        }
    } else if (data && (typeof data === "object")) {
        for (const key in data) {
            const value = data[key];
            if (key.startsWith("volatile")) {
                if (typeof value === "boolean") {
                    data[key] = false;
                } else {
                    data[key] = undefined;
                }
            } else {
                resetVolatile(value);
            }
        }
    }
};

/**
 * Wraps a reducer function which, if the state has been changed on any of the keys which are specified for storage, stores the new state in sessionStorage.
 * Loads the default state (for instance on page load) from the store.
 * @param {Function} reducer
 * @param {Function} defaultState
 * @param {string} nameInStorage
 * @return {Function}
 */
export function sessionStorageWritingReducer(reducer, defaultState, nameInStorage) {
    return (state, action) => {
        if (!state) {
            let storedData;
            try {
                storedData = JSON.parse(sessionStorage[nameInStorage]);
                resetVolatile(storedData);
            } catch (error) {
                storedData = {};
            } finally {
                state = Immutable.merge(defaultState(), storedData, {deep: true});
            }
        }
        const newState = reducer(state, action);
        if (newState != state) {
            try {
                sessionStorage[nameInStorage] = JSON.stringify(newState);
            } catch (error) {
                sessionStorage.clear();
            }
        }

        return newState;
    };
}
